import _ from 'lodash'
import Vue from 'vue'

import { colors, Notify } from 'quasar'
import { DateTime } from 'luxon'
const { getPaletteColor } = colors // https://quasar.dev/quasar-utils/color-utils#Helper---getPaletteColor

const SYSTEM_ALARM_DEFS = {
  HEARTBEAT: {
    key: 'HEARTBEAT',
    name: 'Heartbeat',
    desc: 'Detects Loss of Transmission',
    icon: 'las la-heart'
  },
  DEVICE_FAULT: {
    key: 'DEVICE_FAULT',
    name: 'Device Fault',
    desc: 'Device has reported a fault',
    icon: 'las la-plug'
  },
  CONFIG_FAULT: {
    key: 'CONFIG_FAULT',
    name: 'Configuration Fault',
    desc: 'A software issue with the device configuration',
    icon: 'las la-plug'
  }
}

export const BLANK_ENTITY = {
  name: 'Unknown',
  id: '--'
}

export const BLANK_FE_ENTITY = {
  entity: BLANK_ENTITY,
  links: []
}

export { SYSTEM_ALARM_DEFS as systemAlarmsDefs }

/**
 * @param {*} icon icon from entity object
 */
export function translateIcon (icon) {
  if (icon.startsWith('pethau:') === true) {
    // we strip the "pethau:" part
    const name = icon.substring(7)
    return 'icon icon-' + name
  }
  return icon
}

/**
 * See the enum at pethau-web/src/main/java/io/pethau/domain/Alarm.java
 * @param {number} levelNumber
 */
export function levelToLabel (levelNumber) {
  switch (levelNumber) {
    case -100: return 'Error'
    case -50: return 'Disabled'
    case -1: return 'Normal'
    case 0: return 'Normal'
    case 50: return 'Warning'
    case 100: return 'Critical'
    default: return 'Unknown'
  }
}

/**
 * See the enum at pethau-web/src/main/java/io/pethau/domain/Alarm.java
 * @param {number} levelNumber
 */
export function levelToIcon (levelNumber) {
  switch (levelNumber) {
    case -100: return 'do_distube_on' // 'Error'
    case -50: return 'notifications_off' // 'Disabled'
    case -1: return 'verified_user' // 'Installed'
    case 0: return 'check_box' // 'Normal'
    case 50: return 'error' // 'Warning'
    case 100: return 'warning' // 'Critical'
    default: return 'help' // 'Unknown'
  }
}

export function alarmLabelToLevel (levelName) {
  switch ((levelName || '').toLowerCase()) {
    case 'error': return -100
    case 'disabled': return -50
    case 'normal': return 0
    case 'warning': return 50
    case 'critical': return 100
    default: return -1
  }
}

/**
 * See the enum at pethau-web/src/main/java/io/pethau/domain/Alarm.java
 * @param {number} levelNumber
 */
export function levelToClass (levelNumber) {
  switch (levelNumber) {
    case -100: return 'indigo'
    case -50: return 'blue-grey-4'
    case -1: return 'green-11'
    case 0: return 'positive'
    case 50: return 'warning'
    case 100: return 'negative'
    default: return 'info'
  }
}

/**
 * See the enum at pethau-web/src/main/java/io/pethau/domain/Alarm.java
 * @param {number} levelNumber
 */
export function levelToMapMarkerColor (levelNumber) {
  switch (levelNumber) {
    case -100: return 'indigo'
    case -50: return 'blue-grey-4'
    case -1: return 'light-green-3'
    case 0: return 'green'
    case 50: return 'amber-8'
    case 100: return 'red'
    default: return 'info'
  }
}

/**
 * See the enum at pethau-web/src/main/java/io/pethau/domain/Alarm.java
 * @param {number} levelNumber
 */
export function colourClassToHex (colorClass) {
  if (!colorClass) {
    return getPaletteColor('info')
  }

  var hex = getPaletteColor(colorClass)

  if (!hex) {
    return getPaletteColor('info')
  }

  return hex
}

/**
 * @param {string} alarmKey
 * @param {object} feApp
 */
function findAlarmDef (alarmKey, feApp) {
  if ((!alarmKey) || (alarmKey.match(/.+([\\.:]).+/) === null)) {
    return null
  }

  var parts = alarmKey.split(/[\\.:]/)
  if (parts.length === 2) {
    var moduleId = parts[0]
    var mAlarmKey = parts[1]

    if (moduleId === 'system') {
      return SYSTEM_ALARM_DEFS[mAlarmKey]
    } else {
      var module = _(feApp.modules).filter((m) => { return m.id === moduleId }).first()
      if (!module) { return null }
      return module.alarmDefs[mAlarmKey]
    }
  }
  if (parts.length === 3) {
    var moduleId2 = parts[0]
    var special = parts[1]
    var mFieldKey = parts[2]

    if (special === 'threshold') {
      var module2 = _(feApp.modules).filter((m) => { return m.id === moduleId2 }).first()
      if (!module2) { return null }
      if (module2.fields[mFieldKey]) {
        var field = module2.fields[mFieldKey]
        return {
          key: alarmKey,
          name: field.name + 'Threshold',
          desc: 'Auto Threshold Alarm For: ' + field.name,
          icon: 'las la-tachometer-alt'
        }
      }
    }
  }
}

/**
 * @param {string} stateKey
 * @param {object} feApp
 */
function findStateDef (stateKey, entityDefKey, feApp) {
  if ((!stateKey) || (stateKey.match(/.+([\\.:]).+/) === null)) {
    return null
  }

  var parts = stateKey.split(/[\\.:]/)
  if (parts.length !== 2) {
    return null
  }
  var moduleId = parts[0]
  var mStateKey = parts[1]

  var module = _(feApp.modules).filter((m) => { return m.id === moduleId }).first()
  if (!module) { return null }

  if (module.entityDefs[entityDefKey]) {
    var entityDef = module.entityDefs[entityDefKey]
    if (entityDef.stateDefs[mStateKey]) {
      return entityDef.stateDefs[mStateKey]
    }
  }
}

/**
 * with an deviceDef key from a device, search the app's modules for it.
 * @param {string} deviceDefKey
 * @param {object} feApp
 */
function findDeviceDef (defKey, feApp) {
  for (var m = 0; m < feApp.modules.length; m++) {
    var module = feApp.modules[m]
    if (module.deviceDefs && module.deviceDefs[defKey]) {
      return module.deviceDefs[defKey]
    }
  }
  return {}
}

/**
 * with an entityDef key from an entity, search the app's modules for it.
 * @param {string} entityDefKey
 * @param {object} feApp
 */
function findEntityDef (defKey, feApp) {
  if (!defKey) {
    return // pick up the default
  }

  for (var m = 0; m < feApp.modules.length; m++) {
    var module = feApp.modules[m]
    if (module.entityDefs && module.entityDefs[defKey]) {
      return module.entityDefs[defKey]
    }
  }
}

/**
 * find a stream's StreamDef in an module/entityDef
 * @param {string} entityDefKey
 * @param {object} feApp
 */
function findStreamDef (stream, feApp) {
  if (!stream) { return } // pick up the default
  if (!stream.defKey) { return }
  if (!stream.entityDefKey) { return }
  if (!stream.moduleId) { return }

  return feApp.modulesById[stream.moduleId].entityDefs[stream.entityDefKey].streamDefs[stream.defKey]
}

/**
 * Generate a set of CSS and other FrontEnd (FE) attributes based on the level
 * @param {Alarm} alarm
 */
export function convertLevelToFEAttributes (level) {
  if (_.isObject(level) && level.level) {
    return convertLevelToFEAttributes(level.level)
  }

  if (typeof level === 'number') {
    var cssBgName = levelToClass(level)
    return {
      level: level,
      cssBg: cssBgName,
      label: levelToLabel(level)
    }
  } else if (typeof level === 'string') {
    var levelNum = alarmLabelToLevel(level)
    var cssBgName2 = levelToClass(levelNum)
    return {
      level: levelNum,
      cssBg: cssBgName2,
      label: level // level is in String here
    }
  }
}

/**
 * Convert an entitiy for display adding information from the Modules in the supplied FeApp
 * @param {Entity} entity
 * @param {*} feApp
 */
export function convertFEEntity (entity, feApp, entityLinks) {
  var feEntity = {
    entity: entity,
    alarms: [],
    states: [],
    currentLevel: null,
    links: entityLinks,
    def: null,
    streams: {},
    mode: entity.mode // set to entity mode initially
  }

  if (!feEntity.mode) {
    feEntity.mode = 'Enabled'
  }

  if (feEntity.mode !== 'Archived' && (!entityLinks || entityLinks.length === 0)) {
    feEntity.mode = 'Unlinked'
  }


  if (entity.alarms) {
    _.each(entity.alarms, (alarm, alarmKey) => {
      var alarmDef = findAlarmDef(alarmKey, feApp)
      if (alarmDef) {
        feEntity.alarms.push({
          value: alarm,
          attrs: convertLevelToFEAttributes(alarm),
          def: alarmDef
        })
      }
    })
  }

  var allAlarms = [].concat(feEntity.alarms)

  if (entity.states) {
    _.each(entity.states, (state, stateKey) => {
      var entityDefKey = entity.defId
      var stateDef = findStateDef(stateKey, entityDefKey, feApp)
      if (stateDef) {
        feEntity.states.push({
          value: state,
          // attrs: convertLevelToFEAttributes(alarm),
          def: stateDef
        })
      }
    })
  }

  /* convert all the streams similarly to the entity */
  _.each(entity.streams, (stream) => {
    var fes = {
      stream: stream,
      key: stream.key,
      alarms: [],
      currentLevel: null,
      def: findStreamDef(stream, feApp)
    }

    if (stream.alarms) {
      _.each(stream.alarms, (alarm, alarmKey) => {
        var alarmDef = findAlarmDef(alarmKey, feApp)
        if (alarmDef) {
          fes.alarms.push({
            value: alarm,
            attrs: convertLevelToFEAttributes(alarm),
            def: alarmDef
          })
        }
      })

      allAlarms = allAlarms.concat(fes.alarms)
    }

    if (stream.mode === 'Archived' || stream.mode === 'Disabled') {
      fes.currentLevel = {
        value: { level: -50 },
        attrs: convertLevelToFEAttributes({ level: -50 }),
        def: { name: '--' }
      }
    } else {
      fes.currentLevel = reduceAlarmsToWorstLevel(fes.alarms)
    }


    feEntity.streams[stream.key] = fes
  })
  // end each stream convert
  feEntity.currentLevel = reduceAlarmsToWorstLevel(allAlarms)

  var defaultDef = {
    key: 'DEFAULT',
    type: 'Thing',
    desc: 'Generic Thing',
    icon: 'las la-cube'
  }

  feEntity.def = findEntityDef(entity.defId, feApp) || _.cloneDeep(defaultDef)

  return feEntity
}

/** calculate the worst summary level, using default below in case no alarms */
function reduceAlarmsToWorstLevel (alarmList) {
  var defaultLevel = {
    value: { level: -1 },
    attrs: convertLevelToFEAttributes({ level: -1 }),
    def: { name: '--' }
  }

  return _.reduce(alarmList, (worst, alarm) => {
    if (alarm.attrs.level > worst.attrs.level) {
      return alarm
    } else {
      return worst
    }
  }, defaultLevel)
}

/**
 * Convert an entitiy for display adding information from the Modules in the supplied FeApp
 * @param {Device} device
 * @param {*} feApp
 */
export function convertFEDevice (device, feApp) {
  var feDevice = {
    device: device,
    alarms: [],
    currentLevel: -1,
    def: findDeviceDef(device.defId, feApp)
  }

  if (device.alarms) {
    _.each(device.alarms, (alarm, alarmKey) => {
      var alarmDef = findAlarmDef(alarmKey, feApp)
      if (alarmDef) {
        feDevice.alarms.push({
          value: alarm,
          attrs: convertLevelToFEAttributes(alarm),
          def: alarmDef
        })
      }
    })
  }

  /** calculate the worst summary level, using default below in case no alarms */
  var defaultLevel = {
    value: { level: -1 },
    attrs: convertLevelToFEAttributes({ level: -1 }),
    def: { name: '--' }
  }

  feDevice.currentLevel = _.reduce(feDevice.alarms, (worst, alarm) => {
    if (alarm.attrs.level > worst.attrs.level) {
      return alarm
    } else {
      return worst
    }
  }, defaultLevel)

  // for the devices, pluck out the HEATBEAT alarm
  feDevice.heartbeat = _(feDevice.alarms).filter((a) => { return a.def.key === 'HEARTBEAT' }).first() || _.cloneDeep(defaultLevel)
  return feDevice
}

/**
 * processes all entities for display purposes and provides an index by ID
 * the result is an object comprising
 * 1. a list of FEEntity objects unsorted
 * 2. an index object providing indexed access by ID
 * {
 *   list: [] // list of FEEntities { entitiy, alarms, currentlevel } (see conversion function above)
 *   byId: {
 *        'id1': FEEntity1,
 *        'id2': FEEntity2
 *    },
 *    byType: {
 *      'Group': [ {...}, {...} ],
 *      'Entity': [ {...}, {...} ]
 *    }
 * }
 * @param {list} entities run all entities through the convertor
 * @param {*} feApp
 */
export function convertFEEntities (entities, feApp, links) {
  var list = []

  links = _(links).groupBy('entityId').value() // index them

  _.each(entities, function (e) {
    const eLinks = links[e.id] || []
    var feEntity = convertFEEntity(e, feApp, eLinks)
    list.push(feEntity)
  })

  var byId = _(list).keyBy('entity.id').value() // k:v indexed by ID

  // group list indexed by entity Type: 'Thing', 'Group' etc
  var byType = _(list).groupBy('entity.type').value()

  // ensure there is an empty list in any case
  _.each(['Thing', 'Group'], t => {
    if (!byType[t]) {
      byType[t] = []
    }
  })

  /* if the entity has a groupId ref, link it to the feEntity */
  _.each(list, function (feEntity) {
    if (feEntity.entity.groupId) {
      var feGroup = byId[feEntity.entity.groupId]
      if (feGroup) {
        feEntity.group = feGroup.entity // we link the DB entity not the marked up one that would have no alarms etc
      }
    } else {
      feEntity.group = null
    }
  })

  return {
    list: list,
    byId: byId,
    byType: byType
  }
}

/**
 * processes all devices for display purposes and provides an index by ID
 * the result is an object comprising
 * 1. a list of FEDevice objects unsorted
 * 2. an index object providing indexed access by ID
 * {
 *   list: [] // list of FEDevice (see conversion function above)
 *   byId: {
 *        'id1': FEDevice1,
 *        'id2': FEDevice2
 *    }
 * }
 * @param {list} entities run all entities through the convertor
 * @param {*} feApp
 */
export function convertFEDevices (devices, feApp, links) {
  var list = []

  links = _(links).groupBy('deviceId').value() // index them

  _.each(devices, function (d) {
    var feDevice = convertFEDevice(d, feApp)
    feDevice.links = links[d.id] || []
    list.push(feDevice)
  })

  return {
    list: list,
    byId: _(list).keyBy('device.id').value()
  }
}

export function getDeviceId (deviceIn) {
  var device = deviceIn
  if (!device) { return null }
  if (_.isString(device)) { return device }
  if (_.has(device, 'device')) {
    // an fe device
    device = device.device
  }
  if (_.has(device, 'id')) {
    return device.id
  }
  console.warn('Cannot determine ID for device: "' + JSON.stringify(deviceIn))
  return null
}

export function getEntityId (entity) {
  var e = entity
  if (!entity) { return null }
  if (_.isString(entity)) { return entity }
  if (_.has(entity, 'entity')) {
    // an fe device
    entity = entity.entity
  }
  if (_.has(entity, 'id')) {
    return entity.id
  }
  console.warn('Cannot determine ID for entity: "' + JSON.stringify(e))
  return null
}

export const CommonMethods = Vue.mixin({
  filters: {
    _get (object, path, defaultValue) {
      return _.get(object, path, defaultValue)
    }
  },
  methods: {
    _has (o, path) {
      var has = _.get(o, path)
      return !_.isUndefined(has) && !_.isNull(has)
    },
    _notEmpty (o) {
      return !_.isEmpty(o)
    },
    _empty (o) {
      return _.isEmpty(o)
    },
    showInSideBar (type, o) {
      this.$eventBus.$emit('show-in-sidebar', type, o)
    }
  }
})

export const notifyCatch = function (err) {
  switch (err.response.status) {
    case 401: {
      Notify.error('Session Timeout')
      break // noop, a redirect is coming
    }
    case 400: {
      Notify.error('Bad Request')
      break
    }
    case 404: {
      Notify.error('Not Found')
      break
    }
    case 500: {
      Notify.error('Server Error')
      break
    }
    default: {
      Notify.error('Unknown Error: ' + err.response.status)
    }
  }
}

export function getReportTimestampSuffix () {
  return DateTime.utc().toISO().substring(0, 19).replaceAll('-', '').replaceAll(':', '').replace('T', '-')
}
